import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './InvoicePrintView.css';
import { hoyLocal } from '../utils/fechaLocal';

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  code?: string;
  sku?: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  type: 'A' | 'B' | 'C';
  issueDate: string;
  clientName: string;
  clientDocument?: string;
  clientTaxCondition: string;
  clientAddress?: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  discount?: number;
  items: InvoiceItem[];
  notes?: string;
  paymentMethod?: string;
  afipCae?: string;
  afipCaeVto?: string;
  afipCbteTipo?: number;
  afipCbteNro?: number;
  afipPtoVta?: number;
}

interface CompanySettings {
  name: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  cuit: string;
  instagram: string;
  weekdayHours: string;
  saturdayHours: string;
  logo?: string;
  activityStartDate?: string;
  taxStatus?: string;
  iibb?: string;
}

interface InvoicePrintViewProps {
  invoice: Invoice;
  companyInfo?: {
    name: string;
    address: string;
    cuit: string;
    phone: string;
    email: string;
  };
}

const getCondicionVenta = (pm?: string) => {
  const m = (pm || '').toLowerCase();
  if (m.includes('efectivo') || m === 'cash') return 'Contado';
  if (m.includes('debito') || m === 'debit') return 'Tarjeta de Débito';
  if (m.includes('transferencia') || m === 'transfer') return 'Transferencia Bancaria';
  if (m === 'tc3' || m.includes('tarjeta_3') || m.includes('credito_3')) return 'Tarjeta de Crédito - 3 cuotas';
  if (m === 'tc6' || m.includes('tarjeta_6') || m.includes('credito_6')) return 'Tarjeta de Crédito - 6 cuotas';
  if (m.includes('tarjeta') || m.includes('credito')) return 'Tarjeta de Crédito';
  if (m.includes('cuenta_corriente') || m.includes('cta_cte')) return 'Cuenta Corriente';
  if (m.includes('cheque')) return 'Cheque';
  if (m) return m.charAt(0).toUpperCase() + m.slice(1).replace(/_/g, ' ');
  return 'Contado';
};

const InvoicePrintView: React.FC<InvoicePrintViewProps> = ({ invoice }) => {
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: 'ROCAM S.R.L.',
    address: 'Bv. Oroño 620 - San Lorenzo, Santa Fe',
    phone: '3476409820',
    whatsapp: '3476409820',
    email: 'ventas@rocam.com.ar',
    cuit: '30716642336',
    instagram: '@rocamneumaticos',
    weekdayHours: '',
    saturdayHours: '',
    activityStartDate: '29/10/2019',
    taxStatus: 'IVA Responsable Inscripto',
    iibb: '091-728559-8'
  });

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const endpoint = token ? '/api/settings/company' : '/api/settings/company/public';
        const response = await axios.get(endpoint);
        if (response.data) {
          setCompanySettings(prev => ({
            ...prev,
            name: response.data.name || prev.name,
            address: response.data.address || prev.address,
            phone: response.data.phone || prev.phone,
            whatsapp: response.data.whatsapp || prev.whatsapp,
            email: response.data.email || prev.email,
            cuit: response.data.cuit || prev.cuit,
            instagram: response.data.instagram || prev.instagram,
            weekdayHours: response.data.weekdayHours || prev.weekdayHours,
            saturdayHours: response.data.saturdayHours || prev.saturdayHours,
            logo: response.data.logo || prev.logo,
            activityStartDate: response.data.activityStartDate || prev.activityStartDate,
            taxStatus: response.data.taxStatus || prev.taxStatus,
            iibb: response.data.iibb || prev.iibb,
          }));
        }
      } catch (e) {
        // Usar defaults
      }
    };
    fetchCompanySettings();
  }, []);

  const cleanCuit = (cuit: string): string => cuit.replace(/[-\s]/g, '');

  const formatDate = (dateString: string): string => {
    try {
      const d = new Date(dateString);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch {
      return dateString;
    }
  };

  const formatAfipDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    const clean = dateStr.replace(/[-/]/g, '');
    if (clean.length === 8) {
      return `${clean.slice(6, 8)}/${clean.slice(4, 6)}/${clean.slice(0, 4)}`;
    }
    return formatDate(dateStr);
  };

  const formatNumber = (n: number): string => {
    return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getCbteTypeCode = (type: string): string => {
    const codes: Record<string, string> = { 'A': '001', 'B': '006', 'C': '011' };
    return codes[type] || '006';
  };

  const getCondicionIvaText = (condicion: string): string => {
    const normalized = condicion?.toUpperCase() || '';
    if (normalized.includes('RESPONSABLE') && normalized.includes('INSCRIPTO')) return 'IVA Responsable Inscripto';
    if (normalized.includes('MONOTRIBUTO')) return 'Responsable Monotributo';
    if (normalized.includes('EXENTO')) return 'IVA Sujeto Exento';
    if (normalized.includes('CONSUMIDOR') || normalized === 'CF') return 'Consumidor Final';
    if (normalized === 'RI') return 'IVA Responsable Inscripto';
    if (normalized === 'MT') return 'Responsable Monotributo';
    if (normalized === 'EX') return 'IVA Sujeto Exento';
    return condicion || 'Consumidor Final';
  };

  const formatPtoVta = (): string => {
    return String(invoice.afipPtoVta || 2).padStart(5, '0');
  };

  const formatCbteNro = (): string => {
    return String(invoice.afipCbteNro || 0).padStart(8, '0');
  };

  const getQRUrl = (): string | null => {
    if (!invoice.afipCae || !companySettings.cuit) return null;
    const cuit = cleanCuit(companySettings.cuit);
    const ptoVta = invoice.afipPtoVta || 2;
    const cbteTipo = invoice.afipCbteTipo || (invoice.type === 'A' ? 1 : invoice.type === 'C' ? 11 : 6);
    const cbteNro = invoice.afipCbteNro || 1;
    const fecha = invoice.issueDate?.slice(0, 10) || hoyLocal();
    const docReceptor = cleanCuit(invoice.clientDocument || '0');
    const tipoDocReceptor = docReceptor.length === 11 ? 80 : 96;

    const data = {
      ver: 1, fecha, cuit: Number(cuit), ptoVta, tipoCmp: cbteTipo,
      nroCmp: cbteNro, importe: invoice.total, moneda: 'PES', ctz: 1,
      tipoDocRec: tipoDocReceptor, nroDocRec: Number(docReceptor) || 0,
      tipoCodAut: 'E', codAut: Number(invoice.afipCae)
    };
    return `https://www.afip.gob.ar/fe/qr/?p=${btoa(JSON.stringify(data))}`;
  };

  const handlePrint = () => { window.print(); };

  const isAuthorized = !!(invoice.afipCae);
  const qrUrl = getQRUrl();
  const esFacturaB = invoice.type === 'B';
  // IVA contenido para Factura B (Ley 27.743 Transparencia Fiscal)
  const ivaContenido = esFacturaB ? (invoice.subtotal * (invoice.taxRate || 21)) / (100 + (invoice.taxRate || 21)) : 0;

  return (
    <div className="invoice-print-container">
      {/* Boton Imprimir */}
      <div className="print-hidden" style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <button onClick={handlePrint} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Imprimir Factura
        </button>
      </div>

      <div className="invoice-page">
        {/* === ORIGINAL === */}
        <div className="bill-copy-label">ORIGINAL</div>

        {/* === HEADER EMISOR === */}
        <div className="bill-header">
          {/* Izquierda: Datos empresa */}
          <div className="bill-header-left">
            <div className="bill-company-name">{companySettings.name}</div>
            <div className="bill-emitter-line">
              <span className="bill-label">Raz&oacute;n Social:</span> {companySettings.name}
            </div>
            <div className="bill-emitter-line">
              <span className="bill-label">Domicilio Comercial:</span> {companySettings.address}
            </div>
            <div className="bill-emitter-line">
              <span className="bill-label">Condici&oacute;n frente al IVA:</span> {companySettings.taxStatus}
            </div>
          </div>

          {/* Centro: Letra */}
          <div className="bill-type-container">
            <div className="bill-type-box">
              <span className="bill-type-letter">{invoice.type}</span>
            </div>
            <div className="bill-type-code">COD. {getCbteTypeCode(invoice.type)}</div>
          </div>

          {/* Derecha: Numero y datos fiscales */}
          <div className="bill-header-right">
            <div className="bill-factura-title">FACTURA</div>
            <div className="bill-number-row">
              <span>Punto de Venta: {formatPtoVta()}</span>
              <span style={{ marginLeft: '16px' }}>Comp. Nro: {formatCbteNro()}</span>
            </div>
            <div className="bill-emitter-line">
              <span className="bill-label">Fecha de Emisi&oacute;n:</span> {formatDate(invoice.issueDate)}
            </div>
            <div className="bill-emitter-line">
              <span className="bill-label">CUIT:</span> {cleanCuit(companySettings.cuit)}
            </div>
            <div className="bill-emitter-line">
              <span className="bill-label">Ingresos Brutos:</span> {companySettings.iibb || '-'}
            </div>
            <div className="bill-emitter-line">
              <span className="bill-label">Fecha de Inicio de Actividades:</span> {companySettings.activityStartDate || '-'}
            </div>
          </div>
        </div>

        {/* === DATOS RECEPTOR === */}
        <div className="bill-receptor">
          <div className="bill-receptor-row">
            <div className="bill-receptor-field" style={{ flex: '0 0 40%' }}>
              <span className="bill-label">CUIT:</span> {cleanCuit(invoice.clientDocument || '0')}
            </div>
            <div className="bill-receptor-field" style={{ flex: 1 }}>
              <span className="bill-label">Apellido y Nombre / Raz&oacute;n Social:</span> {invoice.clientName}
            </div>
          </div>
          <div className="bill-receptor-row">
            <div className="bill-receptor-field" style={{ flex: '0 0 40%' }}>
              <span className="bill-label">Condici&oacute;n frente al IVA:</span> {getCondicionIvaText(invoice.clientTaxCondition)}
            </div>
            <div className="bill-receptor-field" style={{ flex: 1 }}>
              <span className="bill-label">Domicilio:</span> {invoice.clientAddress || '-'}
            </div>
          </div>
          <div className="bill-receptor-row">
            <div className="bill-receptor-field">
              <span className="bill-label">Condici&oacute;n de venta:</span> {getCondicionVenta(invoice.paymentMethod)}
            </div>
          </div>
        </div>

        {/* === TABLA DE ITEMS === */}
        <table className="bill-items-table">
          <thead>
            <tr>
              <th className="col-code">C&oacute;digo</th>
              <th className="col-prod">Producto / Servicio</th>
              <th className="col-qty">Cantidad</th>
              <th className="col-unit">U. Medida</th>
              <th className="col-price">Precio Unit.</th>
              <th className="col-bonif-pct">% Bonif</th>
              <th className="col-bonif-imp">Imp. Bonif.</th>
              <th className="col-subtotal">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, index) => (
              <tr key={index}>
                <td className="col-code">{item.code || item.sku || ''}</td>
                <td className="col-prod">{item.description}</td>
                <td className="col-qty">{formatNumber(item.quantity)}</td>
                <td className="col-unit">unidades</td>
                <td className="col-price">{formatNumber(item.unitPrice)}</td>
                <td className="col-bonif-pct">0,00</td>
                <td className="col-bonif-imp">0,00</td>
                <td className="col-subtotal">{formatNumber(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* === TOTALES === */}
        <div className="bill-totals">
          <div className="bill-total-row">
            <span className="bill-total-label">Subtotal: $</span>
            <span className="bill-total-value">{formatNumber(invoice.subtotal)}</span>
          </div>
          <div className="bill-total-row">
            <span className="bill-total-label">Importe Otros Tributos: $</span>
            <span className="bill-total-value">0,00</span>
          </div>
          <div className="bill-total-row bill-total-final">
            <span className="bill-total-label">Importe Total: $</span>
            <span className="bill-total-value">{formatNumber(invoice.total)}</span>
          </div>
        </div>

        {/* === TRANSPARENCIA FISCAL (solo Factura B) === */}
        {esFacturaB && (
          <div className="bill-transparencia">
            <div className="bill-transparencia-title">
              <em>R&eacute;gimen de Transparencia Fiscal al Consumidor (Ley 27.743)</em>
            </div>
            <div className="bill-transparencia-row">
              <span>IVA Contenido: $</span>
              <span style={{ marginLeft: '16px' }}>{formatNumber(ivaContenido)}</span>
            </div>
          </div>
        )}

        {/* === FOOTER: QR + ARCA + CAE === */}
        {isAuthorized && (
          <div className="bill-footer-cae">
            {/* Izquierda: QR + ARCA */}
            <div className="bill-footer-left">
              {qrUrl && (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrUrl)}`}
                  alt="QR AFIP"
                  className="bill-qr-img"
                />
              )}
              <div className="bill-arca-block">
                <div className="bill-arca-name">ARCA</div>
                <div className="bill-arca-sub">AGENCIA DE RECAUDACI&Oacute;N<br/>Y CONTROL ADUANERO</div>
              </div>
            </div>

            {/* Centro */}
            <div className="bill-footer-center">
              <div className="bill-pag">P&aacute;g. 1/1</div>
            </div>

            {/* Derecha: CAE */}
            <div className="bill-footer-right">
              <div className="bill-cae-line">
                <span className="bill-label">CAE N&deg;:</span> {invoice.afipCae}
              </div>
              <div className="bill-cae-line">
                <span className="bill-label">Fecha de Vto. de CAE:</span> {formatAfipDate(invoice.afipCaeVto)}
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {isAuthorized && (
          <div className="bill-disclaimer">
            <em>Comprobante Autorizado</em>
            <p>Esta Agencia no se responsabiliza por los datos ingresados en el detalle de la operaci&oacute;n</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePrintView;
